"use client";

import React, { useEffect } from "react";
import { FiArrowRight, FiCheck } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "next/navigation";
import { openReviewPopup } from "@/Redux/slice/reviewPopupSlice";
import NoReviewIcon from "@/Components/Common/Icons/NoReviewIcon";

/**
 * @param ctaLabel       Override the blue CTA button text. Defaults to
 *                       "Write Anonymous Review" so the FDA product
 *                       detail page renders unchanged.
 * @param headlineLabel  Override the headline text shown next to the wave
 *                       emoji. Defaults to "No reviews yet" — pipeline
 *                       page can pass "No perspectives yet" etc.
 */
const BeFirstToReview = ({
  productName,
  ctaLabel = "Write Anonymous Review",
  headlineLabel = "No reviews yet",
}) => {
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const user = useSelector((state) => state?.userData?.userData?.user);
  const isReps = user?.type === "reps";

  // Auto-open review popup if openReview=true in URL
  useEffect(() => {
    if (isReps) return;
    const shouldOpenReview = searchParams.get("openReview");
    if (shouldOpenReview === "true") {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        const reviewsSection = document.getElementById('reviews');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
        dispatch(openReviewPopup());
      }, 500);
    }
  }, [searchParams, dispatch, isReps]);

  const handleWriteReview = (e) => {
    e.preventDefault();

    setTimeout(() => {
      const reviewsSection = document.getElementById('reviews');
      if (reviewsSection) {
        reviewsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 300);
    dispatch(openReviewPopup());
  };

  // For reps users, show simple "No reviews yet" without CTA
  if (isReps) {
    return (
      <div className="flex flex-col items-center text-center gap-2 py-4">
        <NoReviewIcon />
        <h4 className="text-sm sm:text-base font-bold text-gray-900 font-poppins">
          No Reviews Yet
        </h4>
        <p className="text-xs text-gray-500 font-poppins">
          Check back later for physician insights.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-2xl">👋</span>
        <h4 className="text-base font-bold text-gray-900 font-poppins">
          {headlineLabel} — <span className="text-primary-primaryBlue">be the first!</span>
        </h4>
      </div>

      <p className="text-xs text-gray-500 font-poppins text-right flex items-center gap-1">
        <FiCheck className="w-3 h-3 text-emerald-500" />
        100% anonymous · Verified U.S. prescribers only
      </p>

      <span className="text-[10px] text-gray-400 font-poppins text-right">
        (no name, NPI, or practice ever shown)
      </span>
      {/*
      <p className="text-sm text-gray-600 font-poppins text-right">
        Your <span className="font-medium text-primary-primaryBlue">45-second review</span> helps thousands of colleagues.
      </p> */}

      <button
        onClick={handleWriteReview}
        className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-primaryBlue hover:bg-blue-700 text-white text-sm font-semibold font-poppins transition-all"
      >
        {ctaLabel}
        <FiArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BeFirstToReview;
